'use client';

import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { HeadingSection, SettingsSection } from '@terra/ui-design-system';
import { HStack } from '@terra/ui-styled-system';
import { SettingsHowToUseTooltip } from './SettingsHowToUseTooltip';
import { useSettingsHowToUse } from './settingsHowToUseExamples';
import type { SettingsTabHeaderConfigEntry } from './settingsTabHeaderConfig';
import { useSettingsTabHeaderChildActions } from './settingsTabHeaderContext';

type ActiveSettingsSectionProps = {
  config: SettingsTabHeaderConfigEntry;
  parentActions?: ReactNode;
  fillHeight?: boolean;
  children: ReactNode;
};

export function ActiveSettingsSection({
  config,
  parentActions,
  fillHeight = false,
  children,
}: ActiveSettingsSectionProps) {
  const childActions = useSettingsTabHeaderChildActions();
  const titleNamespace = config.translationNamespace ?? 'settings';
  const descriptionNamespace =
    config.descriptionNamespace ?? config.translationNamespace ?? 'settings';
  const howToUseNamespace = config.howToUseNamespace ?? 'settings';

  const tTitle = useTranslations(titleNamespace);
  const tDescription = useTranslations(descriptionNamespace);
  const howToUse = useSettingsHowToUse({
    translationKey: config.howToUseKey,
    namespace: howToUseNamespace,
  });
  const actions = parentActions ?? childActions;

  return (
    <SettingsSection
      fillHeight={fillHeight}
      title={
        <HStack gap="1" alignItems="center" minW={0}>
          <HeadingSection>{tTitle(config.titleKey)}</HeadingSection>
          <SettingsHowToUseTooltip {...howToUse} />
        </HStack>
      }
      description={tDescription(config.descriptionKey)}
      action={
        actions ? (
          <HStack gap="2" alignItems="center">
            {actions}
          </HStack>
        ) : undefined
      }
    >
      {children}
    </SettingsSection>
  );
}
